import React from 'react';
import { Box, InputBase, Typography } from '@mui/material';
import { IngestionBrandInfo } from '../../services/integrationApi';
import {
  ConnectorFilter,
  ConnectorHealth,
  HEALTH_TONES,
  deriveConnectorHealth,
  needsAttention,
  sidebarSubtitle,
  connectorSourceShortLabel,
  connectorSources,
} from './connectorHealth';
import { chipTone, ellipsis, groupHeading, ingest, tabular } from './ingestionTokens';
import { tokens } from '../../theme/tokens';
import { RunSparkline } from './RunSparkline';

interface ConnectorSidebarProps {
  brands: IngestionBrandInfo[];
  selectedBrandId: string | null;
  filter: ConnectorFilter;
  search: string;
  onFilterChange: (filter: ConnectorFilter) => void;
  onSearchChange: (value: string) => void;
  onSelect: (brandId: string) => void;
}

export function matchesFilter(health: ConnectorHealth, filter: ConnectorFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'attention') return needsAttention(health);
  if (filter === 'failing') return health === 'failing';
  return health === 'healthy';
}

export function listVisibleConnectors(
  brands: IngestionBrandInfo[],
  filter: ConnectorFilter,
  search: string,
  selectedBrandId?: string | null
): IngestionBrandInfo[] {
  const query = search.trim().toLowerCase();
  const filtered = brands.filter((brand) => {
    const health = deriveConnectorHealth(brand);
    if (!matchesFilter(health, filter)) return false;
    if (!query) return true;
    return (
      brand.name.toLowerCase().includes(query) ||
      brand.id.toLowerCase().includes(query) ||
      connectorSources(brand).some((source) => source.toLowerCase().includes(query))
    );
  });
  const selected = selectedBrandId ? brands.find((brand) => brand.id === selectedBrandId) : undefined;
  if (selected && !filtered.some((brand) => brand.id === selected.id)) {
    return [selected, ...filtered];
  }
  return filtered;
}

const KeyCap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      border: `1px solid ${ingest.color.control}`,
      borderRadius: '4px',
      padding: '1px 5px',
      background: ingest.color.surface,
      fontFamily: ingest.font.mono,
      fontSize: '11px',
      lineHeight: 1.4,
    }}
  >
    {children}
  </Box>
);

export const ConnectorSidebar: React.FC<ConnectorSidebarProps> = ({
  brands,
  selectedBrandId,
  filter,
  search,
  onFilterChange,
  onSearchChange,
  onSelect,
}) => {
  const healthById = new Map(brands.map((brand) => [brand.id, deriveConnectorHealth(brand)]));
  const healthOf = (brand: IngestionBrandInfo) => healthById.get(brand.id) ?? deriveConnectorHealth(brand);

  const counts = {
    attention: brands.filter((brand) => needsAttention(healthOf(brand))).length,
    all: brands.length,
    failing: brands.filter((brand) => healthOf(brand) === 'failing').length,
    healthy: brands.filter((brand) => healthOf(brand) === 'healthy').length,
  };

  const visible = listVisibleConnectors(brands, filter, search, selectedBrandId);
  const attentionRows = visible.filter((brand) => needsAttention(healthOf(brand)));
  const healthyRows = visible.filter((brand) => !needsAttention(healthOf(brand)));

  const groups: Array<{
    label: string;
    rows: IngestionBrandInfo[];
    headBg: string;
    headBorder: string;
    headInk: string;
    countInk: string;
  }> = [];
  if (attentionRows.length) {
    groups.push({
      label: 'Needs attention',
      rows: attentionRows,
      headBg: ingest.color.attentionHeadBg,
      headBorder: ingest.color.attentionHeadBorder,
      headInk: ingest.color.attentionHeadInk,
      countInk: ingest.color.attentionHeadCount,
    });
  }
  if (healthyRows.length) {
    groups.push({
      label: 'Healthy',
      rows: healthyRows,
      headBg: ingest.color.sunken,
      headBorder: ingest.color.borderInner,
      headInk: ingest.color.dim,
      countInk: ingest.color.faintAlt,
    });
  }

  const chips: Array<{ id: ConnectorFilter; label: string; count: number }> = [
    { id: 'attention', label: 'Attention', count: counts.attention },
    { id: 'all', label: 'All', count: counts.all },
    { id: 'failing', label: 'Failing', count: counts.failing },
    { id: 'healthy', label: 'Healthy', count: counts.healthy },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        background: ingest.color.sunken,
        borderRight: { md: `1px solid ${ingest.color.borderInner}` },
        borderBottom: { xs: `1px solid ${ingest.color.borderInner}`, md: 0 },
      }}
    >
      <Box
        sx={{
          padding: '14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          borderBottom: `1px solid ${ingest.color.borderInner}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            height: 32,
            padding: '0 11px',
            border: `1px solid ${ingest.color.control}`,
            borderRadius: '7px',
            background: ingest.color.surface,
            '&:focus-within': { borderColor: ingest.color.accent },
          }}
        >
          <Box component="span" aria-hidden sx={{ fontSize: '12px', color: ingest.color.placeholder }}>
            ⌕
          </Box>
          <InputBase
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={`Search ${brands.length} connectors`}
            inputProps={{ 'aria-label': 'Search connectors' }}
            sx={{
              flex: 1,
              fontFamily: ingest.font.sans,
              fontSize: '12.5px',
              color: ingest.color.ink,
              '& input::placeholder': { color: ingest.color.placeholder, opacity: 1 },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {chips.map((chip) => {
            const tone = chipTone(filter === chip.id);
            return (
              <Box
                key={chip.id}
                component="button"
                type="button"
                aria-pressed={filter === chip.id}
                onClick={() => onFilterChange(chip.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  height: 26,
                  padding: '0 9px',
                  border: `1px solid ${tone.border}`,
                  background: tone.bg,
                  borderRadius: '14px',
                  fontFamily: ingest.font.sans,
                  fontSize: '11.5px',
                  fontWeight: tone.weight,
                  color: tone.ink,
                  cursor: 'pointer',
                  '&:hover': { borderColor: filter === chip.id ? tone.border : tokens.ink.disabled },
                }}
              >
                <span>{chip.label}</span>
                <Box component="span" sx={{ ...tabular, color: tone.countInk }}>
                  {chip.count}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {groups.length === 0 && (
          <Typography
            sx={{ padding: '14px', fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}
          >
            No connectors match this filter.
          </Typography>
        )}

        {groups.map((group) => (
          <Box key={group.label}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 14px 7px',
                background: group.headBg,
                borderBottom: `1px solid ${group.headBorder}`,
              }}
            >
              <Box component="span" sx={{ ...groupHeading, color: group.headInk }}>
                {group.label}
              </Box>
              <Box component="span" sx={{ ...tabular, fontSize: '11px', color: group.countInk }}>
                {group.rows.length}
              </Box>
            </Box>

            {group.rows.map((brand) => {
              const health = healthOf(brand);
              const selected = brand.id === selectedBrandId;
              return (
                <Box
                  key={brand.id}
                  component="button"
                  type="button"
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => onSelect(brand.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '9px 14px',
                    border: 0,
                    borderBottom: `1px solid ${ingest.color.borderFaint}`,
                    background: selected ? ingest.color.accentTint : 'transparent',
                    boxShadow: selected ? `inset 2px 0 0 ${ingest.color.accent}` : 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover': { background: selected ? ingest.color.accentTint : ingest.color.rowOpen },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: '7px',
                      height: '7px',
                      borderRadius: '4px',
                      flexShrink: 0,
                      background: HEALTH_TONES[health].dot,
                    }}
                  />
                  <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '12.5px',
                        fontWeight: selected ? 600 : 500,
                        color: ingest.color.ink,
                        ...ellipsis,
                      }}
                    >
                      {brand.name}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontFamily: ingest.font.sans,
                        fontSize: '11px',
                        color: ingest.color.faintAlt,
                        marginTop: '1px',
                        ...ellipsis,
                      }}
                    >
                      {connectorSourceShortLabel(brand)} · {sidebarSubtitle(brand, health)}
                    </Box>
                  </Box>
                  <RunSparkline brand={brand} />
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          padding: '10px 14px',
          borderTop: `1px solid ${ingest.color.borderInner}`,
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          fontFamily: ingest.font.sans,
          fontSize: '11px',
          color: ingest.color.faintAlt,
        }}
      >
        <KeyCap>J</KeyCap>
        <KeyCap>K</KeyCap>
        <span>move ·</span>
        <KeyCap>R</KeyCap>
        <span>run open brand</span>
      </Box>
    </Box>
  );
};

export default ConnectorSidebar;
